import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api, { getErrorMessage } from '@/hooks/useApi';

// Types matching backend responses
export interface GiftCode {
  code: string;
  tokensToGrant: number;
  status: 'active' | 'used' | 'revoked';
  createdAt: string;
  createdBy: string;
  expiresAt?: string;
  usedAt?: string;
  usedBy?: string;
  campaign?: string;
}

export interface CreateGiftCodesInput {
  quantity: number;
  tokensToGrant: number;
  campaign?: string;
  note?: string;
  expiresAt?: string;
}

export interface CreateGiftCodesResponse {
  codes: string[];
  tokensPerCode: number;
}

export interface ListGiftCodesResponse {
  giftCodes: GiftCode[];
  nextCursor?: string;
}

// Query keys for cache management
export const giftCodeKeys = {
  all: ['gift-codes'] as const,
  list: () => [...giftCodeKeys.all, 'list'] as const,
};

/**
 * List gift codes (admin only)
 */
export function useListGiftCodes() {
  return useQuery({
    queryKey: giftCodeKeys.list(),
    queryFn: async () => {
      const response = await api.get<ListGiftCodesResponse>('/admin/gift-codes?limit=100');
      return response.data.giftCodes;
    },
    staleTime: 30_000, // 30 seconds
  });
}

/**
 * Create gift codes (admin only)
 */
export function useCreateGiftCodes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateGiftCodesInput) => {
      const response = await api.post<CreateGiftCodesResponse>('/admin/gift-codes', input);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the list so it refetches
      queryClient.invalidateQueries({ queryKey: giftCodeKeys.list() });
    },
    onError: (error) => {
      console.error('Create gift codes failed:', getErrorMessage(error));
    },
  });
}

/**
 * Revoke a gift code (admin only)
 */
export function useRevokeGiftCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (code: string) => {
      await api.delete(`/admin/gift-codes/${encodeURIComponent(code)}`);
    },
    onSuccess: () => {
      // Invalidate the list so it refetches
      queryClient.invalidateQueries({ queryKey: giftCodeKeys.list() });
    },
    onError: (error) => {
      console.error('Revoke gift code failed:', getErrorMessage(error));
    },
  });
}
